/**
 * Video Plugin
 *
 * Provides VideoEndpoint for canvas routing between video nodes,
 * and a generic VideoCanvas that works with any video source.
 */

// Register video data types
declare module '@nodes/Endpoint' {
  interface DataTypeRegistry {
    video: 'video';
    color: 'color';
    polylines: 'polylines';
  }
}

import type { Plugin, EndpointWithContext } from '@plugins/Plugin';
import { Domain } from '@nodes/Endpoint';
import { VideoEndpoint } from './VideoEndpoint';
import { videoCanvasNodeDef } from './nodes/VideoCanvasNode';
import { colorNodeDef } from './nodes/ColorNode';
import { DataEndpoint } from '../data/DataEndpoint';

export const videoPlugin: Plugin = {
  id: 'video',
  name: 'Video',
  domain: Domain.Video,

  // Generic video canvas node
  nodes: [videoCanvasNodeDef, colorNodeDef],

  // Only 'video' needs a custom VideoEndpoint; color and polylines use standard DataEndpoint
  endpointDataTypes: ['video', 'color', 'polylines'],

  createNode(type: string, id: string, deps: any) {
    const nodeDef = this.nodes!.find(n => n.type === type);
    if (!nodeDef) {
      throw new Error(`Unknown video node type: ${type}`);
    }
    return new nodeDef.ctor(id, deps);
  },

  createEndpoint(config: EndpointWithContext) {
    switch (config.endpoint.dataType) {
      case 'color':
      case 'polylines':
        return new DataEndpoint(config, this.store!);
      case 'video':
        return new VideoEndpoint(config);
    }
  },

  // Register UI components (deferred to avoid circular deps)
  async registerUI({ registerViz }) {
    const { CanvasVisualization } = await import('./ui/CanvasVisualization');
    registerViz('Canvas', CanvasVisualization);
  }
};
